<template>
  <div class="policy" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { deleting: wasDeleted },
    `policy--${policyTypeColor}`,
  ]">
    <div class="row row--policy box box--default"> 
      <div class="row__data row__data--small">
        {{ date }}
      </div>
      <div class="row__data row__data--medium">{{ policyData.category?.code || statics.empty }}</div>


      <div class="row__data row__data--large">
        <EditableComment :comment="policyData.text" :updateComment="updatePotentialPolicyText" />
      </div>

      <div class="row__data row__data--small row__data--pdf icon__pdf"
        :class="{ 'icon__pdf--active': (policyData.files || []).length }" @click="openPolicyFilesModal()">
      </div>

      <div class="row__data row__data--medium"
        :class="{ 'row__data--underline': $checkRolePermission('potential_policy_edit') }">
        <span v-if="$checkRolePermission('potential_policy_edit')"
          @click="getPolicyFormData(policyData.team_id); showEditablePopup = true" style="cursor: pointer">
          <transition name="fade">
            <PopupEditer v-if="showEditablePopup" @togglePopup="showEditablePopup = $event" :items="agents"
              :selected="agent" :label="['first_name', 'last_name']" @submit="changePolicyAgent($event)" />
          </transition>
          {{ name }}
        </span>

        <span v-else>
          {{ name }}
        </span>
      </div>

      <div class="row__data row__data--small row__data--contract icon__contract"
        @click="handleConvertPotentialToPolicy">
      </div>

      <div v-if="$checkRolePermission('potential_policy_edit')"
        class="row__data row__data--small row__data--edit icon__edit" @click="handleOpenPotentialPolicyModal"></div>

      <div v-else style="cursor:no-drop;" class="row__data row__data--small row__data--edit icon__edit"></div>

      <div v-if="$checkRolePermission('potential_policy_delete')"
        class="row__data row__data--small row__data--delete icon__delete" @click="deletePotentialPolicy()"></div>
      <div v-else style="cursor:no-drop;" class="row__data row__data--small row__data--delete icon__delete"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import statics from "@/statics";
import PopupEditer from "@/partials/PopupEditer";
import EditableComment from "@/partials/EditableComment";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import { fetchGlobalCustomer } from "@/helpers/accountDetailHelpers";
import {
  openPotentialPolicyModal,
  convertPotentialToPolicy,
} from "@/helpers/potentialPolicyModalHelpers";

defineOptions({
  name: "PotentialPolicy"
});

const props = defineProps({
  policy: {
    type: Object,
    default: null,
  },
  customer: {
    type: Object,
    default: null,
  },
  index: {
    type: Number,
    default: 0,
  },
});

const store = useStore();
const route = useRoute();
const isAccountDetailsView = computed(() => route.name === "AccountDetails");

// State
const isDeleting = ref(false);
const wasDeleted = ref(false);
const showEditablePopup = ref(false);

const policyData = computed(() => props.policy || props.customer || {});

// Computed
const policyForms = computed(() => store.getters.policyForms);

const date = computed(() =>
  policyData.value.date ? formatDisplayDate(policyData.value.date) : statics.empty
);

const policyTypeColor = computed(() =>
  policyData.value.priority ? `type-${policyData.value.priority.type}` : null
);

const agents = computed(() => policyForms.value.agents);

const agent = computed(() =>
  policyData.value.agent
    ? `${policyData.value.agent.first_name} ${policyData.value.agent.last_name}`
    : statics.empty
);

const name = computed(() => agent.value);

// Methods
const getPolicyFormData = async (id) => {
  await store.dispatch("getPolicyForms", id);
};

const deletePotentialPolicy = async () => {
  isDeleting.value = true;
  await store.dispatch("deletePotentialPolicy", policyData.value.id);
  wasDeleted.value = true;

  if (isAccountDetailsView.value) {
    setTimeout(async () => {
      await fetchGlobalCustomer(route.params.id);
    }, 300);
  } else {
    await store.dispatch("searchPotentialCustomers", buildPotentialCustomerSearchPayload());
  }
};

const changePolicyAgent = async (agentId) => {
  await store.dispatch("updatePotentialPolicyAgent", {
    potentialPolicyId: policyData.value.id,
    agentId,
  });

  if (isAccountDetailsView.value) {
    await fetchGlobalCustomer(route.params.id);
  } else {
    await store.dispatch("searchPotentialCustomers", buildPotentialCustomerSearchPayload());
  }
};

const updatePotentialPolicyText = async (text) => {
  await store.dispatch("updatePotentialPolicyText", {
    potentialPolicyId: policyData.value.id,
    text,
  });

  if (isAccountDetailsView.value) {
    await fetchGlobalCustomer(route.params.id);
  } else {
    await store.dispatch("searchPotentialCustomers", buildPotentialCustomerSearchPayload());
  }
};

const openPolicyFilesModal = () => {
  store.commit("setPolicyFileId", policyData.value.id);
  store.commit("setPolicyFiles", policyData.value.files || []);
  store.commit("setPolicyFilesModalIsPotential", true);
  store.commit("setShowPolicyFilesModal", true);
};

const handleOpenPotentialPolicyModal = () => {
  openPotentialPolicyModal(
    {
      $store: store,
      $route: route,
    },
    policyData.value
  );
};

const handleConvertPotentialToPolicy = () => {
  convertPotentialToPolicy(
    {
      $store: store,
      $route: route,
    },
    policyData.value
  );
};

const buildPotentialCustomerSearchPayload = () => ({
  search: route.query.search || "",
  page: Number(route.query.page) || 1,
  teams: route.query.teams ? route.query.teams.split(",").map(Number) : [],
  agents: route.query.agents ? route.query.agents.split(",").map(Number) : [],
  branchCategories: route.query.branchCategories ? route.query.branchCategories.split(",").map(Number) : [],
  companyTypes: route.query.companyTypes ? route.query.companyTypes.split(",").map(Number) : [],
  priorities: route.query.priorities ? route.query.priorities.split(",").map(Number) : [],
  startDateFrom: route.query.startDateFrom || "",
  startDateTo: route.query.startDateTo || "",
  endDateFrom: route.query.endDateFrom || "",
  endDateTo: route.query.endDateTo || "",
  zip_from: route.query.zip_from || "",
  zip_to: route.query.zip_to || "",
  birthday: route.query.birthday || "",
});

// const handleConvertPotentialToPolicy = async () => {
//   // Passing 'this' context equivalent to the helper
//   await convertPotentialToPolicy({
//     policy: props.policy,
//     index: props.index,
//     $store: store,
//     $route: route,
//     // Add other properties if the helper depends on them
//   });
// };

// const handleOpenPotentialPolicyModal = async () => {
//   await openPotentialPolicyModal({
//     policy: props.policy,
//     $store: store,
//   });
// };
</script>
